import { useLocalization, toast, createMutation, createLazyResource } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { createMagicDoorContext } from '~/contexts/utils';
import { getLeasingErrorCode, getLeasingValidationErrors } from '~/repositories/leasing/leasingErrors';
import { leasingRentalApplicationRepo } from '~/repositories/leasing/rentalApplicationRepository';
import { ApplicationStatus, ApplicationDecision } from '~/swagger/leasing/Api';
import type * as Leasing from '~/swagger/leasing/Api';

type SubResourceField = 'employment' | 'residentialHistory' | 'pets' | 'cars' | 'comments' | 'files' | 'interestedUnits';

export interface LeasingApplicationStore {
  application?: Leasing.RentalApplicationDto;
  loading: boolean;
  reportCapabilities?: Leasing.RentalApplicationReportCapabilitiesDto;
}

interface Pagination {
  current: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const replaceList = <T extends { id: string }>(list: T[] | undefined, item: T): T[] => {
  const arr = list ?? [];
  const idx = arr.findIndex((x) => x.id === item.id);
  if (idx === -1) return [...arr, item];
  const copy = arr.slice();
  copy[idx] = item;
  return copy;
};

export const [LeasingRentalApplicationProvider, useLeasingRentalApplication] = createMagicDoorContext('LeasingRentalApplication', () => {
  const { t } = useLocalization();
  const [applications, setApplications] = createSignal<Leasing.RentalApplicationListDto[]>([]);
  const [listLoading, setListLoading] = createSignal(false);
  const [pagination, setPagination] = createSignal<Pagination>({ current: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [applicationStore, setApplicationStore] = createStore<LeasingApplicationStore>({ loading: false });

  // ---- list ----

  const list = async (filter?: Leasing.GetRentalApplicationsParams) => {
    setListLoading(true);
    try {
      const response = await leasingRentalApplicationRepo.list(filter);
      setApplications(response.items);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
        totalPages: response.totalPages,
      });
    } finally {
      setListLoading(false);
    }
  };

  // ---- single application ----

  const fetchApplication = createMutation(async (applicationId = applicationStore.application?.id) => {
    if (!applicationId) return;
    setApplicationStore('loading', true);
    try {
      const app = await leasingRentalApplicationRepo.get(applicationId);
      setApplicationStore('application', reconcile(app));
    } finally {
      setApplicationStore('loading', false);
    }
  });

  const updateApplication = createMutation(async (payload: Leasing.UpdateRentalApplicationDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const result = await leasingRentalApplicationRepo.update(id, payload);
    setApplicationStore('application', reconcile(result));
    return result;
  });

  const createApplication = async (payload: Leasing.CreateRentalApplicationDto) => {
    const created = await leasingRentalApplicationRepo.create(payload);
    setApplicationStore('application', reconcile(created));
    return created;
  };

  const deleteApplication = async (applicationId: string) => {
    await leasingRentalApplicationRepo.delete(applicationId);
    setApplications((prev) => prev.filter((a) => a.id !== applicationId));
  };

  // ---- lifecycle ----

  const submit = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    try {
      const result = await leasingRentalApplicationRepo.submit(id);
      if (result.success) {
        await fetchApplication(id);
        toast.success(t('Application submitted'));
      } else {
        toast.error(t('The application is not yet ready to submit'));
      }
      return result;
    } catch (err) {
      const code = getLeasingErrorCode(err);
      if (code === 'application_not_qualified') {
        const errors = getLeasingValidationErrors(err);
        return { success: false, errors } satisfies Leasing.SubmitRentalApplicationResult;
      }
      throw err;
    }
  });

  const retract = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const result = await leasingRentalApplicationRepo.retract(id);
    setApplicationStore('application', reconcile(result));
    toast.success(t('Application retracted'));
    return result;
  });

  const setDecision = createMutation(async (decision: Leasing.ApplicationDecision) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const result = await leasingRentalApplicationRepo.setDecision(id, { decision });
    setApplicationStore('application', reconcile(result));
    toast.success(t('Decision saved'));
    return result;
  });

  const convertToTenant = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const result = await leasingRentalApplicationRepo.convertToTenant(id);
    await fetchApplication(id);
    return result;
  });

  const requestInfoUpdate = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.requestInfoUpdate(id);
    toast.success(t('Update request sent'));
  });

  // ---- sub-resource CRUD helper ----

  const makeSubResourceOps = <TItem extends { id: string }, TPayload>(resource: string, field: SubResourceField) => ({
    add: createMutation(async (payload: TPayload) => {
      const id = applicationStore.application?.id;
      if (!id) return;
      const item = await leasingRentalApplicationRepo.addSubResource<TItem, TPayload>(id, resource, payload);
      setApplicationStore('application', field as never, (prev: TItem[] | undefined) => [...(prev ?? []), item] as never);
      return item;
    }),
    update: createMutation(async (params: { itemId: string; payload: TPayload }) => {
      const id = applicationStore.application?.id;
      if (!id) return;
      const item = await leasingRentalApplicationRepo.updateSubResource<TItem, TPayload>(id, resource, params.itemId, params.payload);
      setApplicationStore('application', field as never, (prev: TItem[] | undefined) => replaceList(prev, item) as never);
      return item;
    }),
    remove: createMutation(async (itemId: string) => {
      const id = applicationStore.application?.id;
      if (!id) return;
      await leasingRentalApplicationRepo.deleteSubResource(id, resource, itemId);
      setApplicationStore(
        'application',
        field as never,
        (prev: TItem[] | undefined) => (prev ?? []).filter((x) => x.id !== itemId) as never
      );
    }),
  });

  const employmentOps = makeSubResourceOps<Leasing.RentalApplicationEmploymentDto, Leasing.RentalApplicationEmploymentUpsertDto>(
    'employment',
    'employment'
  );
  const residentialHistoryOps = makeSubResourceOps<
    Leasing.RentalApplicationResidentialHistoryDto,
    Leasing.RentalApplicationResidentialHistoryUpsertDto
  >('residential-history', 'residentialHistory');
  const petOps = makeSubResourceOps<Leasing.RentalApplicationPetDto, Leasing.RentalApplicationPetUpsertDto>('pets', 'pets');
  const carOps = makeSubResourceOps<Leasing.RentalApplicationCarDto, Leasing.RentalApplicationCarUpsertDto>('cars', 'cars');
  const commentOps = makeSubResourceOps<Leasing.RentalApplicationCommentDto, Leasing.RentalApplicationCommentUpsertDto>(
    'comments',
    'comments'
  );

  // ---- landlord verification ----

  const requestLandlordVerification = createMutation(async (residentialHistoryId: string) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.requestLandlordVerification(id, residentialHistoryId);
    await fetchApplication(id);
    toast.success(t('Verification email sent'));
  });

  const resendLandlordVerification = createMutation(async (residentialHistoryId: string) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.resendLandlordVerification(id, residentialHistoryId);
    toast.success(t('Verification email resent'));
  });

  // ---- files (two-phase) ----

  const uploadFile = createMutation(async (params: { file: File; description?: string }) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const session = await leasingRentalApplicationRepo.createFileSession(id, { fileName: params.file.name });

    const form = new FormData();
    Object.entries(session.fields ?? {}).forEach(([k, v]) => form.append(k, v));
    form.append('file', params.file);

    const upload = await fetch(session.endpoint, { method: (session.method || 'POST').toUpperCase(), body: form });
    if (!upload.ok) throw new Error(`Upload failed: ${upload.status}`);

    const committed = await leasingRentalApplicationRepo.commitFileSession(id, session.id, { description: params.description });
    const slim: Leasing.RentalApplicationFileDto = {
      id: committed.id,
      fileId: committed.fileId,
      name: committed.name,
      description: committed.description ?? undefined,
      kind: committed.kind,
      employmentId: committed.employmentId ?? undefined,
      residentialHistoryId: committed.residentialHistoryId ?? undefined,
      created: committed.created,
      updated: committed.updated,
    };
    setApplicationStore('application', 'files', (prev) => [...(prev ?? []), slim]);
    return committed;
  });

  const deleteFile = createMutation(async (fileId: string) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.deleteFile(id, fileId);
    setApplicationStore('application', 'files', (prev) => (prev ?? []).filter((f) => f.id !== fileId));
  });

  // ---- interests ----

  const addInterest = createMutation(async (payload: Leasing.RentalApplicationInterestCreateDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const item = await leasingRentalApplicationRepo.addInterest(id, payload);
    await fetchApplication(id);
    return item;
  });

  const removeInterest = createMutation(async (interestId: string) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.removeInterest(id, interestId);
    await fetchApplication(id);
  });

  // ---- co-applicants ----

  const inviteCoApplicant = createMutation(async (payload: Leasing.InviteCoApplicantDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.inviteCoApplicant(id, payload);
    toast.success(t('Co-applicant invited'));
  });

  const removeCoApplicant = createMutation(async (coApplicantId: string) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.removeCoApplicant(id, coApplicantId);
  });

  // ---- screening / reports ----

  const [reportCapabilitiesResource, reportCapabilitiesActions] = createLazyResource(async () => {
    const id = applicationStore.application?.id;
    if (!id) return undefined;
    const caps = await leasingRentalApplicationRepo.getReportCapabilities(id);
    setApplicationStore('reportCapabilities', caps);
    return caps;
  });

  const skipScreening = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.skipScreening(id);
    await fetchApplication(id);
  });

  const requestReports = createMutation(async (payload: Leasing.RequestReportsDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    const reports = await leasingRentalApplicationRepo.requestReports(id, payload);
    setApplicationStore('application', 'reports', (prev) => {
      const merged = new Map((prev ?? []).map((r) => [r.id, r] as const));
      reports.forEach((r) => merged.set(r.id, r));
      return Array.from(merged.values());
    });
    await reportCapabilitiesActions.refetch();
    return reports;
  });

  const getReportHtml = async (reportId: string): Promise<string | undefined> => {
    const id = applicationStore.application?.id;
    if (!id) return undefined;
    return leasingRentalApplicationRepo.getReportHtml(id, reportId);
  };

  // ---- payment ----

  const skipPayment = createMutation(async () => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.skipPayment(id);
    await fetchApplication(id);
  });

  const recordOfflinePayment = createMutation(async (payload: Leasing.RecordOfflinePaymentDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.recordOfflinePayment(id, payload);
    await fetchApplication(id);
  });

  const refundPayment = createMutation(async (payload: Leasing.RefundPaymentDto) => {
    const id = applicationStore.application?.id;
    if (!id) return;
    await leasingRentalApplicationRepo.refundPayment(id, payload);
    await fetchApplication(id);
  });

  // ---- derived flags ----

  const isSubmitted = createMemo(() => applicationStore.application?.status === ApplicationStatus.Submitted);
  const isApproved = createMemo(() => applicationStore.application?.decision === ApplicationDecision.Approved);
  const isRejected = createMemo(() => applicationStore.application?.decision === ApplicationDecision.Rejected);
  const hasDecision = createMemo(() => {
    const d = applicationStore.application?.decision;
    return d === ApplicationDecision.Approved || d === ApplicationDecision.Rejected || d === ApplicationDecision.Ignored;
  });
  const canConvertToTenant = createMemo(() => isApproved() && !applicationStore.application?.tenantId);
  const canSubmit = createMemo(
    () => applicationStore.application?.status === ApplicationStatus.Draft && !!applicationStore.application?.isQualifiedDraft
  );
  const canRetract = createMemo(() => isSubmitted());
  const canRequestReports = createMemo(() => !!applicationStore.application?.canRequestReports);

  return {
    applicationStore,
    setApplicationStore,
    applications,
    listLoading,
    pagination,
    list,
    fetchApplication,
    updateApplication,
    createApplication,
    deleteApplication,
    submit,
    retract,
    setDecision,
    convertToTenant,
    requestInfoUpdate,
    employmentOps,
    residentialHistoryOps,
    petOps,
    carOps,
    commentOps,
    requestLandlordVerification,
    resendLandlordVerification,
    uploadFile,
    deleteFile,
    addInterest,
    removeInterest,
    inviteCoApplicant,
    removeCoApplicant,
    skipScreening,
    requestReports,
    getReportHtml,
    get reportCapabilities() {
      reportCapabilitiesActions.fetch();
      return reportCapabilitiesResource;
    },
    refetchReportCapabilities: () => reportCapabilitiesActions.refetch(),
    skipPayment,
    recordOfflinePayment,
    refundPayment,
    isSubmitted,
    isApproved,
    isRejected,
    hasDecision,
    canConvertToTenant,
    canSubmit,
    canRetract,
    canRequestReports,
  };
});
